import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiUser, FiMail, FiPhone, FiMapPin, FiSave, FiSearch } = FiIcons;

const CustomerManagement = () => {
  const { savedCustomers, setSavedCustomers } = useInvoice();
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  // Filter customers based on search term
  const filteredCustomers = savedCustomers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.phone && customer.phone.includes(searchTerm))
  );

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || '',
      address: customer.address || ''
    });
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: ''
    });
    setIsAddingNew(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.email) {
      alert('Name and email are required');
      return;
    }

    if (isAddingNew) {
      const newCustomer = {
        ...formData,
        id: Date.now()
      };
      setSavedCustomers(prev => [...prev, newCustomer]);
    } else if (editingCustomer) {
      setSavedCustomers(prev =>
        prev.map(cust =>
          cust.id === editingCustomer.id
            ? { ...cust, ...formData }
            : cust
        )
      );
    }

    setEditingCustomer(null);
    setIsAddingNew(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: ''
    });
  };

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this customer?')) {
      setSavedCustomers(prev => prev.filter(cust => cust.id !== id));
    }
  };

  const handleCancel = () => {
    setEditingCustomer(null);
    setIsAddingNew(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: ''
    });
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative flex-grow">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <motion.button
            onClick={handleAddNew}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
            Add New Customer
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Saved Customers ({filteredCustomers.length})</h2>
          </div>
          <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
            <AnimatePresence>
              {filteredCustomers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <SafeIcon icon={FiUser} className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No customers found</p>
                  <p className="text-sm">
                    {searchTerm ? 'Try adjusting your search criteria' : 'Add your first customer to get started'}
                  </p>
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <motion.div
                    key={customer.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      editingCustomer?.id === customer.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{customer.name}</h3>
                        <div className="mt-1 space-y-1">
                          {customer.email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <SafeIcon icon={FiMail} className="w-3 h-3 mr-2 text-gray-400" />
                              {customer.email}
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <SafeIcon icon={FiPhone} className="w-3 h-3 mr-2 text-gray-400" />
                              {customer.phone}
                            </div>
                          )}
                          {customer.address && (
                            <div className="flex items-center text-sm text-gray-600">
                              <SafeIcon icon={FiMapPin} className="w-3 h-3 mr-2 text-gray-400" />
                              <span className="line-clamp-2">{customer.address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <motion.button
                          onClick={() => handleEdit(customer)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Edit customer"
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={() => handleDelete(customer.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Delete customer"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Edit/Add Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isAddingNew ? 'Add New Customer' : editingCustomer ? 'Edit Customer' : 'Customer Details'}
            </h2>
          </div>
          <div className="p-4">
            {(isAddingNew || editingCustomer) ? (
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Customer name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="customer@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter customer address"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <motion.button
                    onClick={handleSave}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    {isAddingNew ? 'Add Customer' : 'Save Changes'}
                  </motion.button>
                  <motion.button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Cancel
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <SafeIcon icon={FiUser} className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Select a customer to edit or add a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerManagement;